/**
 * Analytics mock data service
 * Provides realistic, filter-aware staging payloads for the Medlink analytics workspace.
 */

const REFERENCE_DATE = new Date('2026-03-31T12:00:00Z');

const CASE_TYPE_PROFILES = {
  all: { share: 1, denialDelta: 0, marginFactor: 1, daysToPayDelta: 0 },
  WC: { share: 0.53, denialDelta: -1.8, marginFactor: 1.08, daysToPayDelta: -4 },
  MVA: { share: 0.28, denialDelta: 2.2, marginFactor: 0.95, daysToPayDelta: 5 },
  PI: { share: 0.19, denialDelta: -0.7, marginFactor: 1.15, daysToPayDelta: 2 },
};

const CASE_TYPE_SEGMENTS = {
  WC: 'Workers Compensation',
  MVA: 'Motor Vehicle',
  PI: 'Personal Injury',
  COMMERCIAL: 'Commercial / Third-Party Insurance',
};

const WINDOW_MULTIPLIERS = {
  last_30_days: 0.45,
  last_60_days: 0.7,
  last_90_days: 1,
  mtd: 0.38,
  qtd: 0.82,
  ytd: 1.2,
  custom: 1,
};

const clone = (value) => JSON.parse(JSON.stringify(value));
const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
const round = (value, digits = 0) => Number(Number(value || 0).toFixed(digits));
const sumBy = (items, selector) => items.reduce((sum, item) => sum + Number(selector(item) || 0), 0);

const addDays = (date, days) => {
  const next = new Date(date);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const addMonths = (date, months) => {
  const next = new Date(date);
  next.setUTCMonth(next.getUTCMonth() + months);
  return next;
};

const formatIsoDate = (date) => date.toISOString().split('T')[0];
const formatMonthShort = (date) => date.toLocaleString('en-US', { month: 'short', timeZone: 'UTC' });
const formatMonthYearShort = (date) => date.toLocaleString('en-US', { month: 'short', year: '2-digit', timeZone: 'UTC' });
const percent = (numerator, denominator, digits = 1) => (denominator ? round((numerator / denominator) * 100, digits) : 0);

const getCaseTypeKey = (caseType) => (CASE_TYPE_PROFILES[caseType] ? caseType : 'all');
const getCaseTypeProfile = (caseType) => CASE_TYPE_PROFILES[getCaseTypeKey(caseType)];

const getWindowFactor = (filters = {}, fallback = 1) => {
  if (filters.months) {
    return clamp(Number(filters.months) / 24, 0.35, 1.6);
  }

  if (filters.dateRange && WINDOW_MULTIPLIERS[filters.dateRange]) {
    return WINDOW_MULTIPLIERS[filters.dateRange];
  }

  const dateInputs = [
    filters.startDate,
    filters.endDate,
    filters.paymentDateStart,
    filters.paymentDateEnd,
    filters.dateOfServiceStart,
    filters.dateOfServiceEnd,
  ].filter(Boolean);

  if (dateInputs.length >= 2) {
    const first = new Date(dateInputs[0]);
    const last = new Date(dateInputs[1]);
    if (!Number.isNaN(first.getTime()) && !Number.isNaN(last.getTime())) {
      const dayDiff = Math.abs((last.getTime() - first.getTime()) / (1000 * 60 * 60 * 24)) + 1;
      return clamp(dayDiff / 90, 0.3, 1.5);
    }
  }

  return fallback;
};

const wrapSuccess = (data) => ({ success: true, code: 200, data });
const wrapThunkData = (data) => ({ data });
const wrapThunkNestedData = (data) => ({ data: { success: true, data } });

const applyAbsoluteScale = (value, scale) => Math.max(0, round(Number(value || 0) * scale));
const applyMoneyScale = (value, scale) => Math.max(0, round(Number(value || 0) * scale));

const INSURANCE_DIRECTORY = [
  { id: '1', name: 'GEICO' },
  { id: '2', name: 'State Farm' },
  { id: '3', name: 'Liberty Mutual' },
  { id: '4', name: 'Allstate' },
  { id: '5', name: 'Progressive' },
  { id: '6', name: 'Travelers' },
  { id: '7', name: 'The Hartford' },
  { id: '8', name: 'Sedgwick' },
  { id: '9', name: 'CNA' },
  { id: '10', name: 'CorVel' },
];

const BILLING_MONTHLY_BASE = [
  { month: 'Apr', billed: 905000, paid: 686000 },
  { month: 'May', billed: 948000, paid: 724000 },
  { month: 'Jun', billed: 982000, paid: 751000 },
  { month: 'Jul', billed: 1008000, paid: 776000 },
  { month: 'Aug', billed: 1045000, paid: 804000 },
  { month: 'Sep', billed: 1089000, paid: 835000 },
  { month: 'Oct', billed: 1112000, paid: 861000 },
  { month: 'Nov', billed: 1148000, paid: 888000 },
  { month: 'Dec', billed: 1192000, paid: 911000 },
  { month: 'Jan', billed: 1216000, paid: 932000 },
  { month: 'Feb', billed: 1249000, paid: 959000 },
  { month: 'Mar', billed: 1298000, paid: 996000 },
];

const BILLING_STATUS_BASE = [
  { status: 'Paid', count: 6510 },
  { status: 'Bill Processed', count: 2780 },
  { status: 'Submitted', count: 2050 },
  { status: 'Pending Docs', count: 1280 },
  { status: 'Denied', count: 942 },
  { status: 'Appeal', count: 284 },
  { status: 'Rebill', count: 401 },
  { status: 'Under Review', count: 617 },
];

const BILLING_TOP_INSURERS_BASE = [
  { insuranceName: 'Liberty Mutual', totalPaid: 1540000, caseType: 'WC' },
  { insuranceName: 'GEICO', totalPaid: 1245000, caseType: 'MVA' },
  { insuranceName: 'State Farm', totalPaid: 1128000, caseType: 'MVA' },
  { insuranceName: 'Travelers', totalPaid: 1046000, caseType: 'WC' },
  { insuranceName: 'Allstate', totalPaid: 918000, caseType: 'PI' },
  { insuranceName: 'Sedgwick', totalPaid: 864000, caseType: 'WC' },
  { insuranceName: 'The Hartford', totalPaid: 758000, caseType: 'WC' },
  { insuranceName: 'Progressive', totalPaid: 712000, caseType: 'MVA' },
];

const BILLING_CASE_MIX_BASE = [
  { caseType: 'WC', count: 8150 },
  { caseType: 'MVA', count: 4280 },
  { caseType: 'PI', count: 2970 },
];

const BILLING_RECENT_DENIALS_BASE = [
  { caseType: 'WC', dateOfService: '2026-03-29', patientName: 'Mia Alvarez', drugName: 'Gabapentin 300mg', insuranceName: 'Sedgwick', denialAmount: 486, latestDenialReason: 'Missing work-status note' },
  { caseType: 'MVA', dateOfService: '2026-03-28', patientName: 'Evan Brooks', drugName: 'Cyclobenzaprine 10mg', insuranceName: 'GEICO', denialAmount: 392, latestDenialReason: 'Incorrect modifier' },
  { caseType: 'PI', dateOfService: '2026-03-27', patientName: 'Lena Morgan', drugName: 'Diclofenac Gel', insuranceName: 'Allstate', denialAmount: 218, latestDenialReason: 'Benefits exhausted' },
  { caseType: 'WC', dateOfService: '2026-03-26', patientName: 'Noah Patel', drugName: 'Pregabalin 75mg', insuranceName: 'Travelers', denialAmount: 574, latestDenialReason: 'Prior auth expired' },
  { caseType: 'MVA', dateOfService: '2026-03-24', patientName: 'Ari Thompson', drugName: 'Ibuprofen 800mg', insuranceName: 'State Farm', denialAmount: 184, latestDenialReason: 'Late filing edit' },
  { caseType: 'PI', dateOfService: '2026-03-22', patientName: 'Sofia Grant', drugName: 'Lidocaine Patch', insuranceName: 'Progressive', denialAmount: 641, latestDenialReason: 'Coverage not active on DOS' },
  { caseType: 'WC', dateOfService: '2026-03-21', patientName: 'Jude Ellis', drugName: 'Methocarbamol 500mg', insuranceName: 'Liberty Mutual', denialAmount: 267, latestDenialReason: 'Missing diagnosis support' },
];

const BILLING_TOP_MEDICATIONS_BASE = [
  { drugName: 'Gabapentin 300mg', count: 1810, totalBilled: 924000, totalPaid: 701000 },
  { drugName: 'Cyclobenzaprine 10mg', count: 1540, totalBilled: 712000, totalPaid: 553000 },
  { drugName: 'Pregabalin 75mg', count: 1320, totalBilled: 868000, totalPaid: 642000 },
  { drugName: 'Ibuprofen 800mg', count: 1280, totalBilled: 441000, totalPaid: 331000 },
  { drugName: 'Lidocaine 5% Patch', count: 1060, totalBilled: 792000, totalPaid: 618000 },
  { drugName: 'Methocarbamol 500mg', count: 940, totalBilled: 382000, totalPaid: 288000 },
];

const BILLING_REBILLS_BASE = [
  { month: 'Oct', rebillCount: 172 },
  { month: 'Nov', rebillCount: 164 },
  { month: 'Dec', rebillCount: 189 },
  { month: 'Jan', rebillCount: 206 },
  { month: 'Feb', rebillCount: 198 },
  { month: 'Mar', rebillCount: 214 },
];

const BILLING_AR_AGING_BASE = [
  { bucket: 'Workers Compensation', caseType: 'WC', '0-30': 362000, '31-60': 241000, '61-90': 149000, '91-120': 82000, '120+': 51000 },
  { bucket: 'Motor Vehicle', caseType: 'MVA', '0-30': 214000, '31-60': 174000, '61-90': 111000, '91-120': 66000, '120+': 44000 },
  { bucket: 'Personal Injury', caseType: 'PI', '0-30': 191000, '31-60': 138000, '61-90': 83000, '91-120': 47000, '120+': 36000 },
  { bucket: 'Commercial', caseType: 'COMMERCIAL', '0-30': 128000, '31-60': 92000, '61-90': 56000, '91-120': 31000, '120+': 18000 },
];

const BILLING_SHORT_PAY_BASE = [
  { payer: 'GEICO', value: 186000, caseType: 'MVA' },
  { payer: 'State Farm', value: 151000, caseType: 'MVA' },
  { payer: 'Liberty Mutual', value: 143000, caseType: 'WC' },
  { payer: 'Travelers', value: 118000, caseType: 'WC' },
  { payer: 'Allstate', value: 94000, caseType: 'PI' },
];

const OPERATIONS_STAGE_BASE = [
  { stage: 'Referral Intake', days: 0.9 },
  { stage: 'Benefits Verification', days: 1.4 },
  { stage: 'Clinical Review', days: 1.1 },
  { stage: 'Prior Auth / Docs', days: 1.8 },
  { stage: 'Billing Ready', days: 0.8 },
  { stage: 'Submission', days: 0.4 },
];

const OPERATIONS_REFERRAL_BASE = [
  { month: 'Oct', rate: 58 },
  { month: 'Nov', rate: 60 },
  { month: 'Dec', rate: 61 },
  { month: 'Jan', rate: 63 },
  { month: 'Feb', rate: 66 },
  { month: 'Mar', rate: 68 },
];

const OPERATIONS_AI_OUTCOMES_BASE = [
  { outcome: 'Resolved live', count: 1140 },
  { outcome: 'Voicemail left', count: 402 },
  { outcome: 'Callback scheduled', count: 286 },
  { outcome: 'Transferred to human', count: 119 },
  { outcome: 'Unable to reach', count: 173 },
];

const OPERATIONS_TEAM_BASE = [
  { team: 'Intake', tasks: 1420 },
  { team: 'Billing', tasks: 1184 },
  { team: 'Concierge', tasks: 934 },
  { team: 'Documents', tasks: 782 },
  { team: 'Reconciliation', tasks: 658 },
];

const WORKLOAD_PROVIDER_BASE = [
  { providerName: 'Dr. Elena Patel', totalScripts: 486, newScripts: 156, totalBilled: 286000 },
  { providerName: 'Dr. Marcus Chen', totalScripts: 452, newScripts: 141, totalBilled: 271000 },
  { providerName: 'Dr. Talia Ross', totalScripts: 438, newScripts: 149, totalBilled: 264000 },
  { providerName: 'Dr. Aaron Kim', totalScripts: 404, newScripts: 126, totalBilled: 238000 },
  { providerName: 'Dr. Priya Shah', totalScripts: 396, newScripts: 118, totalBilled: 233000 },
];

const INSURANCE_PERFORMANCE_BASE = [
  { insuranceId: '1', name: 'GEICO', claims: 1840, billedAmount: 2145000, collectedAmount: 1718000, grossMargin: 702000 },
  { insuranceId: '2', name: 'State Farm', claims: 1710, billedAmount: 1984000, collectedAmount: 1549000, grossMargin: 648000 },
  { insuranceId: '3', name: 'Liberty Mutual', claims: 1932, billedAmount: 2418000, collectedAmount: 1982000, grossMargin: 844000 },
  { insuranceId: '4', name: 'Allstate', claims: 1288, billedAmount: 1496000, collectedAmount: 1184000, grossMargin: 521000 },
  { insuranceId: '5', name: 'Progressive', claims: 1162, billedAmount: 1364000, collectedAmount: 1098000, grossMargin: 454000 },
  { insuranceId: '6', name: 'Travelers', claims: 1424, billedAmount: 1659000, collectedAmount: 1316000, grossMargin: 558000 },
  { insuranceId: '7', name: 'The Hartford', claims: 938, billedAmount: 1084000, collectedAmount: 876000, grossMargin: 366000 },
  { insuranceId: '8', name: 'Sedgwick', claims: 1261, billedAmount: 1518000, collectedAmount: 1253000, grossMargin: 519000 },
  { insuranceId: '9', name: 'CNA', claims: 804, billedAmount: 954000, collectedAmount: 778000, grossMargin: 314000 },
];

const PROVIDER_PERFORMANCE_BASE = [
  { ProviderName: 'Dr. Elena Patel', CaseType: 'WC', TotalBilled: 942000, TotalPaid: 754000, TotalProfit: 308000 },
  { ProviderName: 'Dr. Marcus Chen', CaseType: 'MVA', TotalBilled: 881000, TotalPaid: 676000, TotalProfit: 261000 },
  { ProviderName: 'Dr. Talia Ross', CaseType: 'PI', TotalBilled: 835000, TotalPaid: 669000, TotalProfit: 298000 },
  { ProviderName: 'Dr. Aaron Kim', CaseType: 'WC', TotalBilled: 774000, TotalPaid: 628000, TotalProfit: 242000 },
  { ProviderName: 'Dr. Priya Shah', CaseType: 'MVA', TotalBilled: 748000, TotalPaid: 577000, TotalProfit: 218000 },
  { ProviderName: 'Dr. Noah Feldman', CaseType: 'PI', TotalBilled: 702000, TotalPaid: 561000, TotalProfit: 251000 },
  { ProviderName: 'Dr. Hannah Lopez', CaseType: 'WC', TotalBilled: 668000, TotalPaid: 542000, TotalProfit: 206000 },
  { ProviderName: 'Dr. Victor Greene', CaseType: 'MVA', TotalBilled: 614000, TotalPaid: 487000, TotalProfit: 183000 },
];

const REFERRAL_PROFITABILITY_BASE = [
  { SourceName: 'Hawthorne Law Group', PatientZip: '33176', TotalProfit: 264000, caseType: 'PI' },
  { SourceName: 'Northshore Ortho', PatientZip: '60611', TotalProfit: 238000, caseType: 'WC' },
  { SourceName: 'Summit Spine Center', PatientZip: '77057', TotalProfit: 224000, caseType: 'MVA' },
  { SourceName: 'Lakeview Rehab', PatientZip: '32819', TotalProfit: 198000, caseType: 'WC' },
  { SourceName: 'Riverbend Injury Clinic', PatientZip: '75234', TotalProfit: 183000, caseType: 'MVA' },
  { SourceName: 'Beacon Legal Partners', PatientZip: '10019', TotalProfit: 176000, caseType: 'PI' },
  { SourceName: 'Atlantic Neuro Group', PatientZip: '33324', TotalProfit: 165000, caseType: 'WC' },
  { SourceName: 'Metro Recovery Center', PatientZip: '85016', TotalProfit: 158000, caseType: 'MVA' },
  { SourceName: 'Direct DME Channel', PatientZip: '30328', TotalProfit: 124000, caseType: 'PI' },
];

const COMPLIANCE_RISK_BASE = [
  { segment: 'Workers Compensation', PatientCount: 612, HighRiskCount: 87, OpioidRiskCount: 31 },
  { segment: 'Motor Vehicle', PatientCount: 428, HighRiskCount: 74, OpioidRiskCount: 28 },
  { segment: 'Personal Injury', PatientCount: 366, HighRiskCount: 58, OpioidRiskCount: 24 },
];

const SUPPLY_CHAIN_BASE = [
  { SupplierName: 'McKesson', TotalSpend: 412000, LabelType: 'Generic', TotalQty: 18240, DeaSchedule: 'II' },
  { SupplierName: 'Cardinal Health', TotalSpend: 368000, LabelType: 'Brand', TotalQty: 12480, DeaSchedule: 'III' },
  { SupplierName: 'AmerisourceBergen', TotalSpend: 341000, LabelType: 'Generic', TotalQty: 16620, DeaSchedule: 'IV' },
  { SupplierName: 'Anda', TotalSpend: 294000, LabelType: 'Brand', TotalQty: 9240, DeaSchedule: '' },
  { SupplierName: 'Morris & Dickson', TotalSpend: 271000, LabelType: 'Generic', TotalQty: 13840, DeaSchedule: 'V' },
  { SupplierName: 'HD Smith', TotalSpend: 248000, LabelType: 'Generic', TotalQty: 12080, DeaSchedule: 'II' },
  { SupplierName: 'Cardinal Health', TotalSpend: 194000, LabelType: 'Brand', TotalQty: 6880, DeaSchedule: '' },
  { SupplierName: 'McKesson', TotalSpend: 172000, LabelType: 'Generic', TotalQty: 10120, DeaSchedule: 'III' },
];

const ORDER_TRENDS_BASE = [
  { month: 'Sep', spend: 118000, volume: 4380, predictedSpend: 116000 },
  { month: 'Oct', spend: 123000, volume: 4520, predictedSpend: 121000 },
  { month: 'Nov', spend: 129000, volume: 4680, predictedSpend: 126000 },
  { month: 'Dec', spend: 134000, volume: 4810, predictedSpend: 133000 },
  { month: 'Jan', spend: 141000, volume: 4980, predictedSpend: 139000 },
  { month: 'Feb', spend: 147000, volume: 5120, predictedSpend: 146000 },
  { month: 'Mar', spend: 152000, volume: 5290, predictedSpend: 151000 },
];

const CONTROLLED_SUBSTANCE_BASE = [
  { drug: 'Oxycodone 10mg', ordered: 1480, dispensed: 1454, discrepancy: 26 },
  { drug: 'Hydrocodone 5mg', ordered: 1080, dispensed: 1072, discrepancy: 8 },
  { drug: 'Adderall 20mg', ordered: 940, dispensed: 908, discrepancy: 32 },
  { drug: 'Lorazepam 1mg', ordered: 760, dispensed: 752, discrepancy: 8 },
  { drug: 'Tramadol 50mg', ordered: 1840, dispensed: 1796, discrepancy: 44 },
];

const STOCKOUT_RISK_BASE = [
  { drug: 'Amoxicillin 500mg', onHand: 420, dailyBurn: 78, daysRemaining: 5.4, status: 'Critical' },
  { drug: 'Lisinopril 10mg', onHand: 1180, dailyBurn: 142, daysRemaining: 8.3, status: 'Warning' },
  { drug: 'Atorvastatin 20mg', onHand: 1760, dailyBurn: 172, daysRemaining: 10.2, status: 'Warning' },
  { drug: 'Omeprazole 20mg', onHand: 960, dailyBurn: 81, daysRemaining: 11.9, status: 'Warning' },
  { drug: 'Metformin 500mg', onHand: 4820, dailyBurn: 292, daysRemaining: 16.5, status: 'Healthy' },
];

const GEOGRAPHIC_DISTRIBUTION_BASE = [
  { state: 'FL', count: 4500, coordinates: [27.994402, -81.760254] },
  { state: 'NY', count: 3380, coordinates: [40.712776, -74.005974] },
  { state: 'TX', count: 3010, coordinates: [31.968599, -99.901813] },
  { state: 'IL', count: 2440, coordinates: [40.633125, -89.398529] },
  { state: 'GA', count: 1890, coordinates: [32.165623, -82.900078] },
];

const MEDICATION_CARRIER_BASE = [
  { carrierName: 'Liberty Mutual', caseType: 'WC', totalRx: 2450, avgGP: 512, avgGPP: 32, denialRate: 12, totalBilled: 1832000, totalPaid: 1421000 },
  { carrierName: 'Travelers', caseType: 'WC', totalRx: 1890, avgGP: 485, avgGPP: 30, denialRate: 15, totalBilled: 1384000, totalPaid: 1064000 },
  { carrierName: 'Sedgwick', caseType: 'WC', totalRx: 1540, avgGP: 468, avgGPP: 31, denialRate: 11, totalBilled: 1116000, totalPaid: 872000 },
  { carrierName: 'GEICO', caseType: 'MVA', totalRx: 1540, avgGP: 420, avgGPP: 28, denialRate: 18, totalBilled: 1088000, totalPaid: 826000 },
  { carrierName: 'State Farm', caseType: 'MVA', totalRx: 1220, avgGP: 380, avgGPP: 26, denialRate: 22, totalBilled: 844000, totalPaid: 620000 },
  { carrierName: 'Progressive', caseType: 'MVA', totalRx: 980, avgGP: 364, avgGPP: 27, denialRate: 19, totalBilled: 694000, totalPaid: 523000 },
  { carrierName: 'Allstate', caseType: 'PI', totalRx: 840, avgGP: 680, avgGPP: 41, denialRate: 8, totalBilled: 736000, totalPaid: 602000 },
  { carrierName: 'The Hartford', caseType: 'PI', totalRx: 710, avgGP: 624, avgGPP: 39, denialRate: 9, totalBilled: 618000, totalPaid: 498000 },
  { carrierName: 'CNA', caseType: 'PI', totalRx: 550, avgGP: 602, avgGPP: 38, denialRate: 10, totalBilled: 471000, totalPaid: 382000 },
];

const MEDICATION_DENIAL_REASONS_BASE = [
  {
    carrierName: 'Liberty Mutual',
    caseType: 'WC',
    reasons: [
      { reason: 'Missing work-status note', percentage: 31 },
      { reason: 'Diagnosis support not attached', percentage: 27 },
      { reason: 'Duplicate fill window', percentage: 23 },
      { reason: 'Prior auth required', percentage: 19 },
    ],
  },
  {
    carrierName: 'GEICO',
    caseType: 'MVA',
    reasons: [
      { reason: 'Incorrect modifier', percentage: 35 },
      { reason: 'Missing information', percentage: 26 },
      { reason: 'Late filing', percentage: 21 },
      { reason: 'Policy inactive on DOS', percentage: 18 },
    ],
  },
  {
    carrierName: 'State Farm',
    caseType: 'MVA',
    reasons: [
      { reason: 'Benefits exhausted', percentage: 42 },
      { reason: 'Non-covered service', percentage: 24 },
      { reason: 'Incorrect ICD-10', percentage: 19 },
      { reason: 'Missing police report reference', percentage: 15 },
    ],
  },
  {
    carrierName: 'Allstate',
    caseType: 'PI',
    reasons: [
      { reason: 'Coverage terminated', percentage: 28 },
      { reason: 'Missing attorney LOA', percentage: 26 },
      { reason: 'Duplicate claim', percentage: 24 },
      { reason: 'Fee schedule mismatch', percentage: 22 },
    ],
  },
  {
    carrierName: 'Travelers',
    caseType: 'WC',
    reasons: [
      { reason: 'Prior auth expired', percentage: 30 },
      { reason: 'Form incomplete', percentage: 25 },
      { reason: 'Missing employer verification', percentage: 24 },
      { reason: 'DOS not certified', percentage: 21 },
    ],
  },
  {
    carrierName: 'The Hartford',
    caseType: 'PI',
    reasons: [
      { reason: 'Medical necessity review', percentage: 33 },
      { reason: 'PIP exhausted', percentage: 26 },
      { reason: 'Out-of-network reduction', percentage: 23 },
      { reason: 'Missing treatment plan', percentage: 18 },
    ],
  },
];

const MEDICATION_DRUG_PERFORMANCE_BASE = [
  {
    drugName: 'Gabapentin 300mg Caps',
    totalRx: 1880,
    ndcs: [
      {
        ndc: '60505-0001-01',
        strength: '300mg',
        totalRx: 990,
        carriers: [
          { carrierName: 'Liberty Mutual', caseType: 'WC', totalRx: 372, avgGP: 126, avgGPP: 42, denialCount: 37 },
          { carrierName: 'GEICO', caseType: 'MVA', totalRx: 284, avgGP: 112, avgGPP: 38, denialCount: 51 },
          { carrierName: 'Allstate', caseType: 'PI', totalRx: 187, avgGP: 134, avgGPP: 45, denialCount: 14 },
        ],
      },
      {
        ndc: '60505-0001-05',
        strength: '300mg (bulk)',
        totalRx: 890,
        carriers: [
          { carrierName: 'Travelers', caseType: 'WC', totalRx: 355, avgGP: 138, avgGPP: 44, denialCount: 42 },
          { carrierName: 'State Farm', caseType: 'MVA', totalRx: 238, avgGP: 118, avgGPP: 37, denialCount: 49 },
          { carrierName: 'The Hartford', caseType: 'PI', totalRx: 164, avgGP: 146, avgGPP: 47, denialCount: 16 },
        ],
      },
    ],
  },
  {
    drugName: 'Cyclobenzaprine 10mg Tab',
    totalRx: 1420,
    ndcs: [
      {
        ndc: '51672-4043-01',
        strength: '10mg',
        totalRx: 1420,
        carriers: [
          { carrierName: 'Liberty Mutual', caseType: 'WC', totalRx: 312, avgGP: 92, avgGPP: 35, denialCount: 29 },
          { carrierName: 'GEICO', caseType: 'MVA', totalRx: 298, avgGP: 84, avgGPP: 33, denialCount: 43 },
          { carrierName: 'Allstate', caseType: 'PI', totalRx: 222, avgGP: 88, avgGPP: 36, denialCount: 11 },
          { carrierName: 'Progressive', caseType: 'MVA', totalRx: 186, avgGP: 79, avgGPP: 31, denialCount: 27 },
        ],
      },
    ],
  },
  {
    drugName: 'Pregabalin 75mg Caps',
    totalRx: 1180,
    ndcs: [
      {
        ndc: '00078-0614-15',
        strength: '75mg',
        totalRx: 1180,
        carriers: [
          { carrierName: 'Travelers', caseType: 'WC', totalRx: 304, avgGP: 154, avgGPP: 46, denialCount: 31 },
          { carrierName: 'State Farm', caseType: 'MVA', totalRx: 242, avgGP: 129, avgGPP: 39, denialCount: 48 },
          { carrierName: 'The Hartford', caseType: 'PI', totalRx: 198, avgGP: 162, avgGPP: 49, denialCount: 17 },
        ],
      },
    ],
  },
  {
    drugName: 'Lidocaine 5% Patch',
    totalRx: 960,
    ndcs: [
      {
        ndc: '63481-0687-30',
        strength: '5%',
        totalRx: 960,
        carriers: [
          { carrierName: 'Sedgwick', caseType: 'WC', totalRx: 248, avgGP: 168, avgGPP: 51, denialCount: 18 },
          { carrierName: 'Progressive', caseType: 'MVA', totalRx: 174, avgGP: 144, avgGPP: 43, denialCount: 28 },
          { carrierName: 'CNA', caseType: 'PI', totalRx: 162, avgGP: 171, avgGPP: 52, denialCount: 13 },
        ],
      },
    ],
  },
];

const INSURANCE_DASHBOARD_ROWS = [
  {
    insuranceId: '1',
    name: 'GEICO',
    patientCount: 42,
    totalRx: 1540,
    totalBalance: 214000,
    totalPotentialGP: 486000,
    maxAge: 118,
    contacts: [{ name: 'Jill Cooper', phone: '(800) 555-0121', email: 'claims@geico.example' }],
  },
  {
    insuranceId: '3',
    name: 'Liberty Mutual',
    patientCount: 51,
    totalRx: 2450,
    totalBalance: 248000,
    totalPotentialGP: 642000,
    maxAge: 96,
    contacts: [{ name: 'Nate Walsh', phone: '(800) 555-0182', email: 'wc@liberty.example' }],
  },
  {
    insuranceId: '4',
    name: 'Allstate',
    patientCount: 27,
    totalRx: 840,
    totalBalance: 132000,
    totalPotentialGP: 294000,
    maxAge: 84,
    contacts: [{ name: 'Clara Dean', phone: '(800) 555-0174', email: 'pip@allstate.example' }],
  },
];

const PATIENTS_BY_INSURANCE = {
  '1': [
    { patientId: 'pat-101', firstName: 'Evan', lastName: 'Brooks', name: 'Evan Brooks', totalRx: 18, totalBalance: 4200, totalPotentialGP: 10800, maxAge: 74 },
    { patientId: 'pat-102', firstName: 'Ari', lastName: 'Thompson', name: 'Ari Thompson', totalRx: 15, totalBalance: 3800, totalPotentialGP: 9200, maxAge: 63 },
  ],
  '3': [
    { patientId: 'pat-201', firstName: 'Mia', lastName: 'Alvarez', name: 'Mia Alvarez', totalRx: 24, totalBalance: 5100, totalPotentialGP: 13200, maxAge: 58 },
    { patientId: 'pat-202', firstName: 'Noah', lastName: 'Patel', name: 'Noah Patel', totalRx: 20, totalBalance: 4600, totalPotentialGP: 12100, maxAge: 81 },
  ],
  '4': [
    { patientId: 'pat-301', firstName: 'Lena', lastName: 'Morgan', name: 'Lena Morgan', totalRx: 13, totalBalance: 3400, totalPotentialGP: 8700, maxAge: 48 },
    { patientId: 'pat-302', firstName: 'Sofia', lastName: 'Grant', name: 'Sofia Grant', totalRx: 11, totalBalance: 2900, totalPotentialGP: 7600, maxAge: 69 },
  ],
};

const CLAIMS_BY_PATIENT = {
  'pat-101': [
    { id: 'claim-1001', claimId: 'claim-1001', insuranceClaimId: 'G-42011', dateOfService: '2026-02-12', providerId: 'prov-11', providerName: 'Dr. Marcus Chen', outstandingBalance: 620, totalPotentialGP: 1480, prescriberName: 'Dr. Marcus Chen' },
    { id: 'claim-1002', claimId: 'claim-1002', insuranceClaimId: 'G-42197', dateOfService: '2026-03-04', providerId: 'prov-11', providerName: 'Dr. Marcus Chen', outstandingBalance: 810, totalPotentialGP: 1860, prescriberName: 'Dr. Marcus Chen' },
  ],
  'pat-102': [
    { id: 'claim-1003', claimId: 'claim-1003', insuranceClaimId: 'G-41502', dateOfService: '2026-01-18', providerId: 'prov-18', providerName: 'Dr. Priya Shah', outstandingBalance: 540, totalPotentialGP: 1260, prescriberName: 'Dr. Priya Shah' },
    { id: 'claim-1004', claimId: 'claim-1004', insuranceClaimId: 'G-42266', dateOfService: '2026-03-11', providerId: 'prov-18', providerName: 'Dr. Priya Shah', outstandingBalance: 690, totalPotentialGP: 1670, prescriberName: 'Dr. Priya Shah' },
  ],
  'pat-201': [
    { id: 'claim-2001', claimId: 'claim-2001', insuranceClaimId: 'L-31081', dateOfService: '2026-02-05', providerId: 'prov-08', providerName: 'Dr. Elena Patel', outstandingBalance: 520, totalPotentialGP: 1560, prescriberName: 'Dr. Elena Patel' },
    { id: 'claim-2002', claimId: 'claim-2002', insuranceClaimId: 'L-31872', dateOfService: '2026-03-07', providerId: 'prov-08', providerName: 'Dr. Elena Patel', outstandingBalance: 780, totalPotentialGP: 2110, prescriberName: 'Dr. Elena Patel' },
  ],
  'pat-202': [
    { id: 'claim-2003', claimId: 'claim-2003', insuranceClaimId: 'L-30544', dateOfService: '2026-01-24', providerId: 'prov-17', providerName: 'Dr. Aaron Kim', outstandingBalance: 610, totalPotentialGP: 1490, prescriberName: 'Dr. Aaron Kim' },
    { id: 'claim-2004', claimId: 'claim-2004', insuranceClaimId: 'L-31941', dateOfService: '2026-03-09', providerId: 'prov-17', providerName: 'Dr. Aaron Kim', outstandingBalance: 850, totalPotentialGP: 2280, prescriberName: 'Dr. Aaron Kim' },
  ],
  'pat-301': [
    { id: 'claim-3001', claimId: 'claim-3001', insuranceClaimId: 'A-22011', dateOfService: '2026-02-16', providerId: 'prov-24', providerName: 'Dr. Talia Ross', outstandingBalance: 490, totalPotentialGP: 1360, prescriberName: 'Dr. Talia Ross' },
  ],
  'pat-302': [
    { id: 'claim-3002', claimId: 'claim-3002', insuranceClaimId: 'A-22551', dateOfService: '2026-03-03', providerId: 'prov-24', providerName: 'Dr. Talia Ross', outstandingBalance: 710, totalPotentialGP: 1810, prescriberName: 'Dr. Talia Ross' },
  ],
};

const PATIENT_MEDICATION_PROFILE_BASE = {
  patientId: 'demo-patient',
  medications: [
    {
      name: 'Gabapentin 300mg',
      totalBilled: 1280,
      totalPaid: 964,
      collectionRate: 75.3,
      denialSummary: 'Most denials tied to missing work-status documentation. Add the adjuster note and physician plan before rebilling.',
      recentFills: [
        { dateOfService: '2026-03-12', billedAmount: 322, paidAmount: 322, status: 'paid' },
        { dateOfService: '2026-02-10', billedAmount: 318, paidAmount: 244, status: 'partial_payment', denialReason: 'Modifier review' },
        { dateOfService: '2026-01-14', billedAmount: 320, paidAmount: 0, status: 'denied', denialReason: 'Missing chart notes' },
      ],
    },
    {
      name: 'Cyclobenzaprine 10mg',
      totalBilled: 940,
      totalPaid: 702,
      collectionRate: 74.7,
      denialSummary: 'Stable payer behavior. One recent edit suggests modifier validation before submission.',
      recentFills: [
        { dateOfService: '2026-03-04', billedAmount: 184, paidAmount: 184, status: 'paid' },
        { dateOfService: '2026-02-02', billedAmount: 192, paidAmount: 154, status: 'partial_payment', denialReason: 'Short pay' },
      ],
    },
    {
      name: 'Lidocaine 5% Patch',
      totalBilled: 1460,
      totalPaid: 1186,
      collectionRate: 81.2,
      denialSummary: 'No significant denial themes detected. Continue standard submission workflow.',
      recentFills: [
        { dateOfService: '2026-03-19', billedAmount: 486, paidAmount: 486, status: 'paid' },
        { dateOfService: '2026-02-20', billedAmount: 488, paidAmount: 356, status: 'partial_payment', denialReason: 'Out-of-network reduction' },
      ],
    },
  ],
};

const PATIENT_ACCOUNT_BALANCE_BASE = {
  patientInfo: {
    patientName: 'Mia Alvarez',
    patientDob: '1986-11-09',
  },
  totals: {
    totalBilled: 4860,
    totalPaid: 3380,
    totalOutstanding: 1480,
  },
  doiGroups: [
    {
      dateOfInjury: '2025-12-18',
      subtotalBilled: 2780,
      subtotalPaid: 1970,
      subtotalOutstanding: 810,
      claims: [
        {
          claimId: 'claim-2001',
          insuranceClaimId: 'L-31081',
          dateOfService: '2026-02-05',
          providerName: 'Dr. Elena Patel',
          medicationName: 'Gabapentin 300mg',
          status: 'bill_processed',
          billedAmount: 940,
          paidAmount: 620,
          writeOffAmount: 0,
          outstandingAmount: 320,
          paymentDate: '2026-03-01',
          totalBilled: 940,
          totalPaid: 620,
          totalOutstanding: 320,
        },
        {
          claimId: 'claim-2002',
          insuranceClaimId: 'L-31872',
          dateOfService: '2026-03-07',
          providerName: 'Dr. Elena Patel',
          medicationName: 'Pregabalin 75mg',
          status: 'submitted',
          billedAmount: 1840,
          paidAmount: 1350,
          writeOffAmount: 50,
          outstandingAmount: 490,
          paymentDate: '2026-04-01',
          totalBilled: 1840,
          totalPaid: 1350,
          totalOutstanding: 490,
        },
      ],
    },
    {
      dateOfInjury: '2026-01-22',
      subtotalBilled: 2080,
      subtotalPaid: 1410,
      subtotalOutstanding: 670,
      claims: [
        {
          claimId: 'claim-2003',
          insuranceClaimId: 'L-32102',
          dateOfService: '2026-03-12',
          providerName: 'Dr. Aaron Kim',
          medicationName: 'Lidocaine 5% Patch',
          status: 'partial_payment',
          billedAmount: 1080,
          paidAmount: 820,
          writeOffAmount: 0,
          outstandingAmount: 260,
          paymentDate: '2026-04-15',
          totalBilled: 1080,
          totalPaid: 820,
          totalOutstanding: 260,
        },
        {
          claimId: 'claim-2004',
          insuranceClaimId: 'L-32311',
          dateOfService: '2026-03-24',
          providerName: 'Dr. Aaron Kim',
          medicationName: 'Cyclobenzaprine 10mg',
          status: 'denied',
          billedAmount: 1000,
          paidAmount: 590,
          writeOffAmount: 25,
          outstandingAmount: 410,
          paymentDate: '2026-04-20',
          totalBilled: 1000,
          totalPaid: 590,
          totalOutstanding: 410,
        },
      ],
    },
  ],
};

const REPORT_PREVIEW_BASE = [
  { id: 'preview-1', reportName: 'Monthly Revenue Rollup', generatedAt: '2026-04-01', status: 'ready' },
  { id: 'preview-2', reportName: 'Denial Root Cause Summary', generatedAt: '2026-04-02', status: 'ready' },
  { id: 'preview-3', reportName: 'Carrier Margin Snapshot', generatedAt: '2026-04-03', status: 'queued' },
];

const buildDailySeries = (days, producer) =>
  Array.from({ length: days }, (_, index) => {
    const dayDate = addDays(REFERENCE_DATE, -(days - 1 - index));
    return producer(dayDate, index);
  });

const buildMonthlySeries = (months, producer) =>
  Array.from({ length: months }, (_, index) => {
    const monthDate = addMonths(REFERENCE_DATE, -(months - 1 - index));
    return producer(monthDate, index);
  });

const SCRIPT_VOLUME_MONTHLY_BASE = buildMonthlySeries(36, (date, index) => {
  const monthFactor = [0.97, 1.01, 1.04, 1.06, 1.03, 1.07, 1.12, 1.08, 1.01, 0.99, 1.03, 1.09][date.getUTCMonth()];
  const totalScripts = Math.round((720 + index * 18) * monthFactor);
  const trueNewPatientScripts = Math.round(totalScripts * 0.16);
  const newPrescriptionScripts = Math.round(totalScripts * 0.22);
  const refillScripts = totalScripts - trueNewPatientScripts - newPrescriptionScripts;
  const newScripts = trueNewPatientScripts + newPrescriptionScripts;
  const existingPatientScripts = totalScripts - trueNewPatientScripts;
  const payerShares = [
    { payerSegment: CASE_TYPE_SEGMENTS.WC, share: 0.47 },
    { payerSegment: CASE_TYPE_SEGMENTS.MVA, share: 0.24 },
    { payerSegment: CASE_TYPE_SEGMENTS.PI, share: 0.18 },
    { payerSegment: CASE_TYPE_SEGMENTS.COMMERCIAL, share: 0.11 },
  ];

  const payerMix = payerShares.map((segment, segmentIndex) => {
    const segmentTotal = Math.round(totalScripts * segment.share);
    const segmentNewScripts = Math.round(newScripts * segment.share * (segmentIndex === 0 ? 1.04 : 0.98));
    const segmentRefillScripts = Math.max(0, segmentTotal - segmentNewScripts);
    const segmentTrueNewPatients = Math.round(trueNewPatientScripts * segment.share * (segmentIndex === 3 ? 0.82 : 1));
    return {
      payerSegment: segment.payerSegment,
      totalScripts: segmentTotal,
      newScripts: segmentNewScripts,
      refillScripts: segmentRefillScripts,
      trueNewPatients: segmentTrueNewPatients,
      shareOfMonth: percent(segmentTotal, totalScripts, 1),
    };
  });

  return {
    month: formatMonthYearShort(date),
    monthStart: formatIsoDate(addDays(date, -date.getUTCDate() + 1)),
    monthEnd: formatIsoDate(date),
    totalScripts,
    newScripts,
    newPrescriptionScripts,
    refillScripts,
    trueNewPatientScripts,
    existingPatientScripts,
    payerMix,
  };
});

const computeMedicationKpis = (carriers) => {
  const totalRx = sumBy(carriers, (carrier) => carrier.totalRx);
  const totalBilled = sumBy(carriers, (carrier) => carrier.totalBilled);
  const totalPaid = sumBy(carriers, (carrier) => carrier.totalPaid);
  const totalGrossProfit = sumBy(carriers, (carrier) => carrier.avgGP * carrier.totalRx);
  const totalGrossMarginPoints = sumBy(carriers, (carrier) => carrier.avgGPP * carrier.totalRx);
  const totalDenials = round(sumBy(carriers, (carrier) => carrier.totalRx * (carrier.denialRate / 100)));

  return {
    totalRx,
    wcRx: sumBy(carriers.filter((carrier) => carrier.caseType === 'WC'), (carrier) => carrier.totalRx),
    mvaRx: sumBy(carriers.filter((carrier) => carrier.caseType === 'MVA'), (carrier) => carrier.totalRx),
    piRx: sumBy(carriers.filter((carrier) => carrier.caseType === 'PI'), (carrier) => carrier.totalRx),
    avgGP: totalRx ? round(totalGrossProfit / totalRx, 2) : 0,
    avgGPP: totalRx ? round(totalGrossMarginPoints / totalRx, 1) : 0,
    avgDenialRate: totalRx ? round((totalDenials / totalRx) * 100, 1) : 0,
    totalDenials,
    totalBilled,
    totalPaid,
  };
};

const buildFilteredMedicationData = (filters = {}) => {
  const selectedCaseTypes = Array.isArray(filters.caseTypes) && filters.caseTypes.length ? filters.caseTypes : null;
  const selectedInsurances = Array.isArray(filters.insuranceNames) && filters.insuranceNames.length ? filters.insuranceNames : null;

  const carrierGP = clone(MEDICATION_CARRIER_BASE).filter((carrier) => {
    if (selectedCaseTypes && !selectedCaseTypes.includes(carrier.caseType)) return false;
    if (selectedInsurances && !selectedInsurances.includes(carrier.carrierName)) return false;
    return true;
  });

  const denialReasons = clone(MEDICATION_DENIAL_REASONS_BASE).filter((entry) => {
    if (selectedCaseTypes && !selectedCaseTypes.includes(entry.caseType)) return false;
    if (selectedInsurances && !selectedInsurances.includes(entry.carrierName)) return false;
    return true;
  });

  const drugPerformance = clone(MEDICATION_DRUG_PERFORMANCE_BASE)
    .map((drug) => {
      const ndcs = drug.ndcs
        .map((ndc) => {
          const carriers = (ndc.carriers || []).filter((carrier) => {
            if (selectedCaseTypes && !selectedCaseTypes.includes(carrier.caseType)) return false;
            if (selectedInsurances && !selectedInsurances.includes(carrier.carrierName)) return false;
            return true;
          });
          return {
            ...ndc,
            carriers,
            totalRx: sumBy(carriers, (carrier) => carrier.totalRx),
          };
        })
        .filter((ndc) => ndc.totalRx > 0);

      return {
        ...drug,
        ndcs,
        totalRx: sumBy(ndcs, (ndc) => ndc.totalRx),
      };
    })
    .filter((drug) => drug.totalRx > 0);

  return {
    kpis: computeMedicationKpis(carrierGP),
    availableInsurances: clone(INSURANCE_DIRECTORY),
    availableCaseTypes: ['WC', 'MVA', 'PI'],
    carrierGP,
    denialReasons,
    drugPerformance,
  };
};

export const mockAnalyticsData = buildFilteredMedicationData();

const buildBillingPerformance = (filters = {}) => {
  const caseType = getCaseTypeKey(filters.caseType);
  const caseProfile = getCaseTypeProfile(caseType);
  const scale = caseType === 'all' ? getWindowFactor(filters, 1) : caseProfile.share * getWindowFactor(filters, 1);
  const denialRate = clamp(13.7 + caseProfile.denialDelta, 6, 28);
  const avgDaysToPay = round(42 + caseProfile.daysToPayDelta, 1);
  const claimsProcessed = applyAbsoluteScale(15402, scale);
  const totalBilled = applyMoneyScale(12450000, scale);
  const totalPaid = applyMoneyScale(totalBilled * 0.75, caseProfile.marginFactor);
  const writeOffAmount = round(totalBilled * (denialRate / 100) * 0.36);
  const outstandingAR = Math.max(0, totalBilled - totalPaid - writeOffAmount);
  const grossProfit = round((totalPaid - claimsProcessed * 246) * caseProfile.marginFactor);
  const avgBilled = claimsProcessed ? round(totalBilled / claimsProcessed) : 0;
  const avgAcquisition = round(246 / caseProfile.marginFactor);
  const avgGrossProfit = claimsProcessed ? round(grossProfit / claimsProcessed) : 0;

  const monthlyTrends = BILLING_MONTHLY_BASE.map((month, index) => {
    const monthScale = scale * (0.96 + index * 0.01);
    return {
      month: month.month,
      billed: applyMoneyScale(month.billed, monthScale),
      paid: applyMoneyScale(month.paid, monthScale * caseProfile.marginFactor),
    };
  });

  const claimsByStatus = BILLING_STATUS_BASE.map((entry) => ({
    ...entry,
    count: applyAbsoluteScale(entry.count, scale),
  }));

  const topInsurers = BILLING_TOP_INSURERS_BASE
    .filter((entry) => caseType === 'all' || entry.caseType === caseType)
    .map((entry) => ({
      insuranceName: entry.insuranceName,
      totalPaid: applyMoneyScale(entry.totalPaid, caseType === 'all' ? getWindowFactor(filters, 1) : getWindowFactor(filters, 1.05)),
    }))
    .sort((left, right) => right.totalPaid - left.totalPaid);

  const caseTypeMix = BILLING_CASE_MIX_BASE
    .filter((entry) => caseType === 'all' || entry.caseType === caseType)
    .map((entry) => ({
      caseType: entry.caseType,
      count: applyAbsoluteScale(entry.count, getWindowFactor(filters, 1)),
    }));

  const recentDenials = BILLING_RECENT_DENIALS_BASE
    .filter((entry) => caseType === 'all' || entry.caseType === caseType)
    .map((entry) => ({
      ...entry,
      denialAmount: applyMoneyScale(entry.denialAmount, caseType === 'all' ? 1 : 1.04),
    }));

  const topMedications = BILLING_TOP_MEDICATIONS_BASE.map((entry) => ({
    ...entry,
    count: applyAbsoluteScale(entry.count, scale),
    totalBilled: applyMoneyScale(entry.totalBilled, scale),
    totalPaid: applyMoneyScale(entry.totalPaid, scale * caseProfile.marginFactor),
  }));

  const rebillByMonth = BILLING_REBILLS_BASE.map((entry, index) => ({
    month: entry.month,
    rebillCount: applyAbsoluteScale(entry.rebillCount, scale * (0.94 + index * 0.015)),
  }));

  const arBuckets = BILLING_AR_AGING_BASE
    .filter((bucket) => caseType === 'all' || bucket.caseType === caseType)
    .map((bucket) => {
      const nextBucket = {
        bucket: bucket.bucket,
        caseType: bucket.caseType,
        '0-30': applyMoneyScale(bucket['0-30'], scale),
        '31-60': applyMoneyScale(bucket['31-60'], scale),
        '61-90': applyMoneyScale(bucket['61-90'], scale),
        '91-120': applyMoneyScale(bucket['91-120'], scale),
        '120+': applyMoneyScale(bucket['120+'], scale),
      };
      nextBucket['90+'] = nextBucket['91-120'] + nextBucket['120+'];
      return nextBucket;
    });

  const totalOutstanding = sumBy(arBuckets, (bucket) => (
    bucket['0-30'] + bucket['31-60'] + bucket['61-90'] + bucket['91-120'] + bucket['120+']
  ));

  const totalUnderpaid = sumBy(
    BILLING_SHORT_PAY_BASE.filter((payer) => caseType === 'all' || payer.caseType === caseType),
    (payer) => applyMoneyScale(payer.value, scale)
  );

  return {
    kpis: {
      totalBilled,
      totalPaid,
      grossProfit,
      outstandingAR,
      claimsProcessed,
      denialRate,
      avgDaysToPay,
      writeOffAmount,
      avgBilled,
      avgAcquisition,
      avgGrossProfit,
    },
    monthlyTrends,
    claimsByStatus,
    topInsurers,
    caseTypeMix,
    recentDenials,
    topMedications,
    rebillByMonth,
    arAging: {
      buckets: arBuckets,
      totalOutstanding,
    },
    waterfall: {
      collectionRate: percent(totalPaid, totalBilled),
      stages: [
        { name: 'Billed', value: totalBilled, type: 'neutral' },
        { name: 'Write-Offs', value: -writeOffAmount, type: 'negative' },
        { name: 'Short Pay', value: -totalUnderpaid, type: 'negative' },
        { name: 'Collected', value: totalPaid, type: 'positive' },
        { name: 'Outstanding', value: outstandingAR, type: 'neutral' },
      ],
    },
    writeOffTrend: {
      averageWriteOffRate: denialRate * 0.4,
      months: monthlyTrends.slice(-6).map((month, index) => ({
        month: month.month,
        billed: month.billed,
        writeOff: round(month.billed * (0.052 + index * 0.002)),
      })),
    },
    shortPayAnalysis: {
      totalUnderpaid,
      payers: BILLING_SHORT_PAY_BASE
        .filter((payer) => caseType === 'all' || payer.caseType === caseType)
        .map((payer) => ({
          payer: payer.payer,
          value: applyMoneyScale(payer.value, scale),
        })),
    },
  };
};

const buildClaimsBillingPerformance = (filters = {}) => {
  const caseType = getCaseTypeKey(filters.caseType);
  const caseProfile = getCaseTypeProfile(caseType);
  const scale = caseType === 'all' ? getWindowFactor(filters, 1) : caseProfile.share * getWindowFactor(filters, 1);
  const denialRate = clamp(13.7 + caseProfile.denialDelta, 6, 28);
  const avgDaysToPay = round(42 + caseProfile.daysToPayDelta, 1);
  const uniqueClaims = Math.floor(applyAbsoluteScale(15402, scale) / 4);
  const totalBilled = applyMoneyScale(12450000, scale);
  const totalPaid = applyMoneyScale(totalBilled * 0.75, caseProfile.marginFactor);
  const writeOffAmount = round(totalBilled * (denialRate / 100) * 0.36);
  const outstandingAR = Math.max(0, totalBilled - totalPaid - writeOffAmount);
  const grossProfit = round((totalPaid - uniqueClaims * 246) * caseProfile.marginFactor);
  const avgBilled = uniqueClaims ? round(totalBilled / uniqueClaims) : 0;
  const avgAcquisition = round(246 / caseProfile.marginFactor);
  const avgGrossProfit = uniqueClaims ? round(grossProfit / uniqueClaims) : 0;

  const monthlyTrends = BILLING_MONTHLY_BASE.map((month, index) => {
    const monthScale = scale * (0.96 + index * 0.01);
    return {
      month: month.month,
      billed: applyMoneyScale(month.billed, monthScale),
      paid: applyMoneyScale(month.paid, monthScale * caseProfile.marginFactor),
    };
  });

  const claimsByStatus = BILLING_STATUS_BASE.map((entry) => ({
    ...entry,
    count: applyAbsoluteScale(entry.count, scale),
  }));

  const topInsurers = BILLING_TOP_INSURERS_BASE
    .filter((entry) => caseType === 'all' || entry.caseType === caseType)
    .map((entry) => ({
      insuranceName: entry.insuranceName,
      totalPaid: applyMoneyScale(entry.totalPaid, caseType === 'all' ? getWindowFactor(filters, 1) : getWindowFactor(filters, 1.05)),
    }))
    .sort((left, right) => right.totalPaid - left.totalPaid);

  const caseTypeMix = BILLING_CASE_MIX_BASE
    .filter((entry) => caseType === 'all' || entry.caseType === caseType)
    .map((entry) => ({
      caseType: entry.caseType,
      count: applyAbsoluteScale(entry.count, getWindowFactor(filters, 1)),
    }));

  const recentDenials = BILLING_RECENT_DENIALS_BASE
    .filter((entry) => caseType === 'all' || entry.caseType === caseType)
    .map((entry) => ({
      ...entry,
      denialAmount: applyMoneyScale(entry.denialAmount, caseType === 'all' ? 1 : 1.04),
    }));

  const topClaims = [
    { claimId: 'CLM-001', patientName: 'John Smith', insuranceName: 'Aetna', count: 4, totalBilled: applyMoneyScale(8500, scale), totalPaid: applyMoneyScale(6800, scale * caseProfile.marginFactor) },
    { claimId: 'CLM-002', patientName: 'Sarah Johnson', insuranceName: 'Blue Cross', count: 3, totalBilled: applyMoneyScale(7200, scale), totalPaid: applyMoneyScale(5760, scale * caseProfile.marginFactor) },
    { claimId: 'CLM-003', patientName: 'Michael Brown', insuranceName: 'United Healthcare', count: 5, totalBilled: applyMoneyScale(10500, scale), totalPaid: applyMoneyScale(8400, scale * caseProfile.marginFactor) },
    { claimId: 'CLM-004', patientName: 'Emily Davis', insuranceName: 'Cigna', count: 2, totalBilled: applyMoneyScale(4800, scale), totalPaid: applyMoneyScale(3840, scale * caseProfile.marginFactor) },
    { claimId: 'CLM-005', patientName: 'Robert Wilson', insuranceName: 'Humana', count: 4, totalBilled: applyMoneyScale(9200, scale), totalPaid: applyMoneyScale(7360, scale * caseProfile.marginFactor) },
    { claimId: 'CLM-006', patientName: 'Jennifer Taylor', insuranceName: 'Aetna', count: 3, totalBilled: applyMoneyScale(6300, scale), totalPaid: applyMoneyScale(5040, scale * caseProfile.marginFactor) },
  ];

  const rebillByMonth = BILLING_REBILLS_BASE.map((entry, index) => ({
    month: entry.month,
    rebillCount: applyAbsoluteScale(entry.rebillCount, scale * (0.94 + index * 0.015)),
  }));

  const arBuckets = BILLING_AR_AGING_BASE
    .filter((bucket) => caseType === 'all' || bucket.caseType === caseType)
    .map((bucket) => {
      const nextBucket = {
        bucket: bucket.bucket,
        caseType: bucket.caseType,
        '0-30': applyMoneyScale(bucket['0-30'], scale),
        '31-60': applyMoneyScale(bucket['31-60'], scale),
        '61-90': applyMoneyScale(bucket['61-90'], scale),
        '91-120': applyMoneyScale(bucket['91-120'], scale),
        '120+': applyMoneyScale(bucket['120+'], scale),
      };
      nextBucket['90+'] = nextBucket['91-120'] + nextBucket['120+'];
      return nextBucket;
    });

  const totalOutstanding = sumBy(arBuckets, (bucket) => (
    bucket['0-30'] + bucket['31-60'] + bucket['61-90'] + bucket['91-120'] + bucket['120+']
  ));

  const totalUnderpaid = sumBy(
    BILLING_SHORT_PAY_BASE.filter((payer) => caseType === 'all' || payer.caseType === caseType),
    (payer) => applyMoneyScale(payer.value, scale)
  );

  const avgRowsPerClaim = 3.8;
  const avgPatientsPerClaim = 1.1;
  const avgProvidersPerClaim = 1.3;

  return {
    kpis: {
      totalBilled,
      totalPaid,
      grossProfit,
      outstandingAR,
      claimsProcessed: uniqueClaims,
      denialRate,
      avgDaysToPay,
      writeOffAmount,
      avgBilled,
      avgAcquisition,
      avgGrossProfit,
      avgRowsPerClaim,
      avgPatientsPerClaim,
      avgProvidersPerClaim,
    },
    monthlyTrends,
    claimsByStatus,
    topInsurers,
    caseTypeMix,
    recentDenials,
    topClaims,
    rebillByMonth,
    arAging: {
      buckets: arBuckets,
      totalOutstanding,
    },
    waterfall: {
      collectionRate: percent(totalPaid, totalBilled),
      stages: [
        { name: 'Billed', value: totalBilled, type: 'neutral' },
        { name: 'Write-Offs', value: -writeOffAmount, type: 'negative' },
        { name: 'Short Pay', value: -totalUnderpaid, type: 'negative' },
        { name: 'Collected', value: totalPaid, type: 'positive' },
        { name: 'Outstanding', value: outstandingAR, type: 'neutral' },
      ],
    },
    writeOffTrend: {
      averageWriteOffRate: denialRate * 0.4,
      months: monthlyTrends.slice(-6).map((month, index) => ({
        month: month.month,
        billed: month.billed,
        writeOff: round(month.billed * (0.052 + index * 0.002)),
      })),
    },
    shortPayAnalysis: {
      totalUnderpaid,
      payers: BILLING_SHORT_PAY_BASE
        .filter((payer) => caseType === 'all' || payer.caseType === caseType)
        .map((payer) => ({
          payer: payer.payer,
          value: applyMoneyScale(payer.value, scale),
        })),
    },
  };
};

const buildOperationsEfficiency = (filters = {}) => {
  const teamScale = filters.team && filters.team !== 'all'
    ? { intake: 0.93, billing: 1.08, concierge: 0.88 }[filters.team] || 1
    : 1;
  const windowFactor = getWindowFactor(filters, 1);

  return {
    stageCycleTime: OPERATIONS_STAGE_BASE.map((entry, index) => ({
      stage: entry.stage,
      days: round(entry.days * teamScale * (1 + index * 0.03), 1),
    })),
    referralConversion: OPERATIONS_REFERRAL_BASE.map((entry, index) => ({
      month: entry.month,
      rate: clamp(round(entry.rate + (teamScale - 1) * 10 + index * 0.3, 1), 0, 100),
    })),
    aiCallOutcomes: OPERATIONS_AI_OUTCOMES_BASE.map((entry) => ({
      outcome: entry.outcome,
      count: applyAbsoluteScale(entry.count, windowFactor * teamScale),
    })),
    teamPerformance: OPERATIONS_TEAM_BASE
      .filter((entry) => filters.team === 'all' || !filters.team || entry.team.toLowerCase() === filters.team)
      .map((entry) => ({
        team: entry.team,
        tasks: applyAbsoluteScale(entry.tasks, windowFactor),
      })),
  };
};

const buildWorkloadIntensity = (filters = {}) => {
  const caseProfile = getCaseTypeProfile(filters.caseType);
  const scale = (getCaseTypeKey(filters.caseType) === 'all' ? 1 : caseProfile.share) * getWindowFactor(filters, 1);

  const dailyLog = buildDailySeries(21, (date, index) => {
    const baseline = 128 + index * 5 + (index % 4) * 8;
    const refill = 176 + index * 4 + ((index + 2) % 5) * 7;
    return {
      date: date.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
      newScripts: applyAbsoluteScale(baseline, scale),
      refillScripts: applyAbsoluteScale(refill, scale),
      providerCount: applyAbsoluteScale(12 + (index % 3), getCaseTypeKey(filters.caseType) === 'all' ? 1 : clamp(caseProfile.share * 2.2, 0.7, 1.4)),
    };
  });

  const totalNewScripts = sumBy(dailyLog, (entry) => entry.newScripts);
  const totalRefillScripts = sumBy(dailyLog, (entry) => entry.refillScripts);
  const totalScripts = totalNewScripts + totalRefillScripts;
  const projectedNextMonthScripts = round(totalScripts * 1.08);

  const staffingModel = buildDailySeries(8, (_, index) => ({
    week: `W${index + 1}`,
    weightedUnits: applyAbsoluteScale(420 + index * 24, scale),
    recommendedFte: round((10.5 + index * 0.25) * (getCaseTypeKey(filters.caseType) === 'all' ? 1.5 : clamp(caseProfile.share * 3, 0.7, 1.3)), 1),
  }));

  return {
    kpis: {
      totalScripts,
      uniquePatients: applyAbsoluteScale(totalScripts * 0.47, 1),
      newScripts: totalNewScripts,
      refillScripts: totalRefillScripts,
      newPatientScripts: applyAbsoluteScale(totalNewScripts * 0.48, 1),
      highRiskScripts: applyAbsoluteScale(totalNewScripts * 0.17, 1),
      currentRecommendedFte: staffingModel[staffingModel.length - 1]?.recommendedFte || 0,
      projectedNextMonthScripts,
    },
    dailyLog,
    newVsRefill: {
      newShare: percent(totalNewScripts, totalScripts),
      refillShare: percent(totalRefillScripts, totalScripts),
      grossProfitNew: applyMoneyScale(totalNewScripts * 118, caseProfile.marginFactor),
      grossProfitRefill: applyMoneyScale(totalRefillScripts * 82, caseProfile.marginFactor),
    },
    patientOnboarding: {
      newPatientScripts: applyAbsoluteScale(totalNewScripts * 0.48, 1),
      protocolScripts: applyAbsoluteScale(totalRefillScripts * 0.56, 1),
      highRiskNewPatients: applyAbsoluteScale(totalNewScripts * 0.15, 1),
      engagedPatients: applyAbsoluteScale(totalScripts * 0.42, 1),
    },
    intakeOrigin: [
      { channel: 'Patient portal', scripts: applyAbsoluteScale(962, scale), notes: applyAbsoluteScale(404, scale) },
      { channel: 'Provider fax', scripts: applyAbsoluteScale(812, scale), notes: applyAbsoluteScale(332, scale) },
      { channel: 'Call center', scripts: applyAbsoluteScale(608, scale), notes: applyAbsoluteScale(461, scale) },
      { channel: 'Law firm referral', scripts: applyAbsoluteScale(388, scale), notes: applyAbsoluteScale(194, scale) },
      { channel: 'In-person intake', scripts: applyAbsoluteScale(241, scale), notes: applyAbsoluteScale(126, scale) },
    ],
    hourlyVolume: Array.from({ length: 11 }, (_, hourIndex) => ({
      hour: hourIndex + 8,
      scripts: applyAbsoluteScale(28 + hourIndex * 7 + (hourIndex > 4 ? 12 : 0), scale),
      notes: applyAbsoluteScale(14 + hourIndex * 5 + (hourIndex > 5 ? 10 : 0), scale),
    })),
    providerWorkload: WORKLOAD_PROVIDER_BASE.map((provider) => ({
      providerName: provider.providerName,
      totalScripts: applyAbsoluteScale(provider.totalScripts, scale),
      newScripts: applyAbsoluteScale(provider.newScripts, scale),
      totalBilled: applyMoneyScale(provider.totalBilled, scale),
    })),
    staffingModel,
    scalability: buildMonthlySeries(8, (date, index) => {
      const scripts = applyAbsoluteScale(520 + index * 26 + (index % 2) * 18, scale);
      return {
        month: formatMonthShort(date),
        totalScripts: scripts,
        projectedNextMonthScripts: round(scripts * 1.07),
        monthOverMonthGrowth: round(3.2 + index * 0.6, 1),
      };
    }),
  };
};

const buildScriptVolumeMix = (filters = {}) => {
  const requestedMonths = clamp(Number(filters.months || 24), 12, 36);
  const caseType = getCaseTypeKey(filters.caseType);
  const monthSlice = SCRIPT_VOLUME_MONTHLY_BASE.slice(-requestedMonths);

  const monthly = monthSlice.map((month) => {
    if (caseType === 'all') {
      return clone(month);
    }

    const payerSegment = CASE_TYPE_SEGMENTS[caseType];
    const segment = month.payerMix.find((entry) => entry.payerSegment === payerSegment);
    const scale = segment ? segment.totalScripts / month.totalScripts : CASE_TYPE_PROFILES[caseType].share;
    const totalScripts = segment?.totalScripts || 0;
    const trueNewPatientScripts = applyAbsoluteScale(month.trueNewPatientScripts, scale);
    const newPrescriptionScripts = applyAbsoluteScale(month.newPrescriptionScripts, scale);
    const refillScripts = Math.max(0, totalScripts - trueNewPatientScripts - newPrescriptionScripts);
    const newScripts = trueNewPatientScripts + newPrescriptionScripts;

    return {
      ...month,
      totalScripts,
      newScripts,
      newPrescriptionScripts,
      refillScripts,
      trueNewPatientScripts,
      existingPatientScripts: Math.max(0, totalScripts - trueNewPatientScripts),
      payerMix: segment ? [segment] : [],
    };
  });

  const summary = monthly.reduce((accumulator, month) => {
    accumulator.totalScripts += month.totalScripts || 0;
    accumulator.newScripts += month.newScripts || 0;
    accumulator.newPrescriptionScripts += month.newPrescriptionScripts || 0;
    accumulator.refillScripts += month.refillScripts || 0;
    accumulator.trueNewPatientScripts += month.trueNewPatientScripts || 0;
    accumulator.existingPatientScripts += month.existingPatientScripts || 0;
    month.payerMix.forEach((segment) => {
      const current = accumulator.payerMixMap[segment.payerSegment] || {
        payerSegment: segment.payerSegment,
        totalScripts: 0,
        newScripts: 0,
        refillScripts: 0,
        trueNewPatients: 0,
        shareOfMonth: 0,
      };
      current.totalScripts += segment.totalScripts || 0;
      current.newScripts += segment.newScripts || 0;
      current.refillScripts += segment.refillScripts || 0;
      current.trueNewPatients += segment.trueNewPatients || 0;
      accumulator.payerMixMap[segment.payerSegment] = current;
    });
    return accumulator;
  }, {
    totalScripts: 0,
    newScripts: 0,
    newPrescriptionScripts: 0,
    refillScripts: 0,
    trueNewPatientScripts: 0,
    existingPatientScripts: 0,
    payerMixMap: {},
  });

  const payerMix = Object.values(summary.payerMixMap)
    .map((segment) => ({
      ...segment,
      shareOfMonth: percent(segment.totalScripts, summary.totalScripts),
    }))
    .sort((left, right) => right.totalScripts - left.totalScripts);

  return {
    period: {
      startDate: monthly[0]?.monthStart || '',
      endDate: monthly[monthly.length - 1]?.monthEnd || '',
      months: requestedMonths,
    },
    summary: {
      totalScripts: summary.totalScripts,
      newScripts: summary.newScripts,
      newPrescriptionScripts: summary.newPrescriptionScripts,
      refillScripts: summary.refillScripts,
      trueNewPatientScripts: summary.trueNewPatientScripts,
      trueNewPatients: applyAbsoluteScale(summary.trueNewPatientScripts, 0.72),
      existingPatientScripts: summary.existingPatientScripts,
      uniquePatients: applyAbsoluteScale(summary.totalScripts, 0.49),
      totalBilled: applyMoneyScale(summary.totalScripts * 612, 1),
      payerMix,
    },
    monthly,
    generatedAt: formatIsoDate(REFERENCE_DATE),
  };
};

const buildClaimsDashboard = (filters = {}) => {
  const caseType = getCaseTypeKey(filters.caseType);
  const scale = (caseType === 'all' ? 1 : CASE_TYPE_PROFILES[caseType].share) * getWindowFactor(filters, filters.dateRange === 'ytd' ? 1.18 : 1);

  const dailyMetrics = buildDailySeries(14, (date, index) => ({
    date: date.toLocaleString('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }),
    rxCount: applyAbsoluteScale(438 + index * 12 + (index % 3) * 14, scale),
    claims: applyAbsoluteScale(171 + index * 6 + (index % 2) * 8, scale),
  }));

  const totalRx = sumBy(dailyMetrics, (entry) => entry.rxCount);
  const totalClaims = sumBy(dailyMetrics, (entry) => entry.claims);
  const totalPatients = applyAbsoluteScale(totalRx, 0.44);
  const totalBilled = applyMoneyScale(totalRx * 418, 1);

  const caseTypeDistribution = BILLING_CASE_MIX_BASE
    .filter((entry) => caseType === 'all' || entry.caseType === caseType)
    .map((entry) => ({ name: entry.caseType, value: applyAbsoluteScale(entry.count, getWindowFactor(filters, 1)) }));

  const topInsurance = [
    { name: 'Liberty Mutual', rxCount: applyAbsoluteScale(982, scale), caseType: 'WC' },
    { name: 'GEICO', rxCount: applyAbsoluteScale(914, scale), caseType: 'MVA' },
    { name: 'State Farm', rxCount: applyAbsoluteScale(868, scale), caseType: 'MVA' },
    { name: 'Allstate', rxCount: applyAbsoluteScale(622, scale), caseType: 'PI' },
    { name: 'Travelers', rxCount: applyAbsoluteScale(594, scale), caseType: 'WC' },
  ].filter((entry) => caseType === 'all' || entry.caseType === caseType);

  const topPrescribers = [
    { name: 'Dr. Elena Patel', billed: applyMoneyScale(412000, scale), caseType: 'WC' },
    { name: 'Dr. Marcus Chen', billed: applyMoneyScale(396000, scale), caseType: 'MVA' },
    { name: 'Dr. Talia Ross', billed: applyMoneyScale(358000, scale), caseType: 'PI' },
    { name: 'Dr. Aaron Kim', billed: applyMoneyScale(337000, scale), caseType: 'WC' },
    { name: 'Dr. Priya Shah', billed: applyMoneyScale(322000, scale), caseType: 'MVA' },
  ].filter((entry) => caseType === 'all' || entry.caseType === caseType);

  const topAttorneys = [
    { name: 'Hawthorne Law Group', billed: applyMoneyScale(542000, scale), caseType: 'PI' },
    { name: 'Beacon Legal Partners', billed: applyMoneyScale(468000, scale), caseType: 'PI' },
    { name: 'Northshore Ortho', billed: applyMoneyScale(441000, scale), caseType: 'WC' },
    { name: 'Summit Spine Center', billed: applyMoneyScale(413000, scale), caseType: 'MVA' },
    { name: 'Riverbend Injury Clinic', billed: applyMoneyScale(382000, scale), caseType: 'MVA' },
  ].filter((entry) => caseType === 'all' || entry.caseType === caseType);

  return {
    dailyMetrics,
    caseTypeDistribution,
    topInsurance,
    topPrescribers,
    topAttorneys,
    kpis: {
      totalRx,
      totalClaims,
      totalPatients,
      totalBilled,
      avgRxPerClaim: totalClaims ? round(totalRx / totalClaims, 2) : 0,
      avgRxPerPatient: totalPatients ? round(totalRx / totalPatients, 2) : 0,
    },
  };
};

const buildProviderPerformance = (filters = {}) => {
  const caseType = getCaseTypeKey(filters.caseType);
  const sortKey = filters.sortBy || 'TotalProfit';
  const topN = Number(filters.topN || 20);

  return clone(PROVIDER_PERFORMANCE_BASE)
    .filter((provider) => caseType === 'all' || provider.CaseType === caseType)
    .sort((left, right) => Number(right[sortKey] || 0) - Number(left[sortKey] || 0))
    .slice(0, topN);
};

const buildReferralProfitability = (filters = {}) => {
  const caseType = getCaseTypeKey(filters.caseType);
  const topN = Number(filters.topN || 50);

  return clone(REFERRAL_PROFITABILITY_BASE)
    .filter((referral) => caseType === 'all' || referral.caseType === caseType)
    .sort((left, right) => right.TotalProfit - left.TotalProfit)
    .slice(0, topN);
};

const buildSupplyChainMetrics = (filters = {}) => {
  const schedule = String(filters.schedule || '').trim();
  return clone(SUPPLY_CHAIN_BASE).filter((entry) => !schedule || entry.DeaSchedule === schedule);
};

const buildInsurancePerformance = (filters = {}) => {
  const sortBy = filters.sortBy || 'collectedAmount';
  const topN = Number(filters.topN || INSURANCE_PERFORMANCE_BASE.length);

  return clone(INSURANCE_PERFORMANCE_BASE)
    .map((entry) => ({
      ...entry,
      collectionRate: percent(entry.collectedAmount, entry.billedAmount),
    }))
    .sort((left, right) => Number(right[sortBy] || 0) - Number(left[sortBy] || 0))
    .slice(0, topN);
};

export const getMockMedicationStats = (filters = {}) => wrapThunkNestedData(buildFilteredMedicationData(filters));

export const getMockBillingPerformance = (filters = {}) => wrapThunkData(buildBillingPerformance(filters));

export const getMockClaimsBillingPerformance = (filters = {}) => wrapThunkData(buildClaimsBillingPerformance(filters));

export const getMockOperationsEfficiency = (filters = {}) => wrapThunkData(buildOperationsEfficiency(filters));

export const getMockClaimsDashboard = (filters = {}) => wrapThunkData(buildClaimsDashboard(filters));

export const getMockWorkloadIntensity = (filters = {}) => wrapThunkData(buildWorkloadIntensity(filters));

export const getMockScriptVolumeMix = (filters = {}) => wrapThunkData(buildScriptVolumeMix(filters));

export const getMockProviderPerformance = (filters = {}) => wrapThunkData(buildProviderPerformance(filters));

export const getMockReferralProfitability = (filters = {}) => wrapThunkData(buildReferralProfitability(filters));

export const getMockComplianceAnalytics = () => wrapThunkData(clone(COMPLIANCE_RISK_BASE));

export const getMockOrderAnalytics = () => wrapThunkNestedData({
  trends: clone(ORDER_TRENDS_BASE),
  controlled: clone(CONTROLLED_SUBSTANCE_BASE),
  stockouts: clone(STOCKOUT_RISK_BASE),
});

export const getMockSupplyChainMetrics = (filters = {}) => wrapThunkData(buildSupplyChainMetrics(filters));

export const getMockInsurancePerformance = (filters = {}) => wrapThunkData(buildInsurancePerformance(filters));

export const getMockGeographicDistribution = () => wrapSuccess(clone(GEOGRAPHIC_DISTRIBUTION_BASE));

export const getMockPatientMedicationProfile = (patientId) => wrapSuccess({
  ...clone(PATIENT_MEDICATION_PROFILE_BASE),
  patientId,
});

export const getMockPatientAccountBalance = (patientId) => wrapSuccess({
  ...clone(PATIENT_ACCOUNT_BALANCE_BASE),
  patientInfo: {
    ...PATIENT_ACCOUNT_BALANCE_BASE.patientInfo,
    patientId,
  },
});

export const getMockInsuranceDashboard = () => wrapSuccess(clone(INSURANCE_DASHBOARD_ROWS));

export const getMockPatientsByInsurance = (insuranceId) => wrapSuccess(clone(PATIENTS_BY_INSURANCE[insuranceId] || []));

export const getMockClaimsByInsuranceAndPatient = (insuranceId, patientId) => {
  if (patientId === 'all') {
    const patientRows = PATIENTS_BY_INSURANCE[insuranceId] || [];
    const claims = patientRows.flatMap((patient) => CLAIMS_BY_PATIENT[patient.patientId] || []);
    return wrapSuccess(clone(claims));
  }

  return wrapSuccess(clone(CLAIMS_BY_PATIENT[patientId] || []));
};

export const getMockReportPreview = () => wrapSuccess(clone(REPORT_PREVIEW_BASE));
